import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import dayjs from 'dayjs';

import { IClient, IConsultation } from '../../../store/types';
import { getTime } from '../../../utils/getTime';
import { buildConsultationInviteText } from '../../../utils/buildConsultationInviteText';
import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { useGetSpecializationsQuery } from '../../../services/directus.api';
import { EditPencilIcon } from '../../icons/edit-pencil';
import { TrashIcon } from '../../icons/trash';

import { Button, Modal, IconButton } from '../../../shared/ui';
import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';

interface ConsultationHoverPopupProps {
  consultation: IConsultation;
  client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy?: () => void;
}

export const ConsultationHoverPopup: FC<ConsultationHoverPopupProps> = ({
  consultation,
  client,
  onClose,
  onEdit,
  onDelete,
  onCopy,
}) => {
  const navigate = useNavigate();
  const userData = useAppSelector(selectUser);
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const [copy, setCopy] = useState(false);
  useEffect(() => {
    if (copy) {
      const timer = setTimeout(() => {
        setCopy(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copy]);

  const handleCopyLink = () => {
    if (!consultation.link) return;

    const defaultSpecialization = 'Психолог';
    const specializationItems = specializationResponse?.data ?? [];
    const selectedSpecialization = specializationItems.find(
      (option) => String(option.id) === String(userData?.specialization ?? ''),
    );
    const specializationText = selectedSpecialization?.title || defaultSpecialization;
    const psychNameFromUserFallback = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ');

    const text = buildConsultationInviteText(consultation, {
      specializationText,
      psychNameFromUserFallback,
      clientFullName: client?.full_name,
    });

    void navigator.clipboard.writeText(text).then(
      () => {
        setCopy(true);
        toast.success('Скопировано');
      },
      () => toast.error('Не удалось скопировать'),
    );
    onCopy?.();
  };

  const isGroupTherapy = consultation.is_group === true;

  const handleOpenTranscription = () => {
    navigateToJournalSession(navigate, consultation.id, consultation.client_entity, 'Транскрипция');
  };

  const title = client?.full_name || 'Встреча';
  const consultationDate = dayjs(consultation.date).format('DD.MM.YYYY');
  const timeStart = getTime(new Date(consultation.datetime_start));
  const timeEnd = getTime(new Date(consultation.datetime_end));
  const sessionNumber = consultation.consultation_number || 1;

  // Извлекаем короткую ссылку из полной ссылки
  const shortLink = consultation.link ? consultation.link.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  return (
    <Modal
      isOpen
      title={title}
      closeOnBackdrop
      onClose={onClose}
      headerActions={
        <>
          <IconButton label="Редактировать" className="group" onClick={onEdit}>
            <EditPencilIcon
              width={28}
              height={28}
              stroke="currentColor"
              className="text-ink-subtle group-hover:text-primary"
            />
          </IconButton>
          <IconButton label="Удалить" className="group" onClick={onDelete}>
            <TrashIcon
              width={28}
              height={28}
              stroke="currentColor"
              className="text-ink-subtle group-hover:text-primary"
            />
          </IconButton>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Параметры */}
        <div className="flex flex-col gap-3">
          {/* Дата */}
          <div className="flex items-center gap-2">
            <span className="text-body-l-strong text-ink">Дата:</span>
            <span className="text-body-l font-normal text-ink">{consultationDate}</span>
          </div>

          {/* Время */}
          <div className="flex items-center gap-2">
            <span className="text-body-l-strong text-ink">Время:</span>
            <span className="text-body-l font-normal text-ink">
              {timeStart} - {timeEnd}
            </span>
          </div>

          {/* Название группы (если групповая терапия) */}
          {isGroupTherapy && (
            <div className="flex items-center gap-2">
              <span className="text-body-l-strong text-ink">Название группы:</span>
              <span className="text-body-l font-normal text-ink">{title}</span>
            </div>
          )}

          {/* Сессия */}
          <div className="flex items-center gap-2">
            <span className="text-body-l-strong text-ink">Сессия</span>
            <span className="text-body-l font-normal text-ink">№{sessionNumber}</span>
          </div>
        </div>

        {/* Ссылка */}
        <div className="flex items-center gap-2">
          <span className="text-body-l-strong text-ink">Поделиться ссылкой</span>
          <div className="flex items-center gap-2">
            <span className="text-body-l font-normal text-primary">{shortLink}</span>
            <IconButton label="Копировать ссылку" className="-m-1" onClick={handleCopyLink}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 11C6 8.172 6 6.757 6.879 5.879C7.757 5 9.172 5 12 5H15C17.828 5 19.243 5 20.121 5.879C21 6.757 21 8.172 21 11V16C21 18.828 21 20.243 20.121 21.121C19.243 22 17.828 22 15 22H12C9.172 22 7.757 22 6.879 21.121C6 20.243 6 18.828 6 16V11Z"
                  stroke="#CC0000"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 19C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16V10C3 6.229 3 4.343 4.172 3.172C5.343 2 7.229 2 11 2H15C15.7956 2 16.5587 2.31607 17.1213 2.87868C17.6839 3.44129 18 4.20435 18 5"
                  stroke="#CC0000"
                  strokeWidth="1.5"
                />
              </svg>
            </IconButton>
          </div>
        </div>

        {/* Кнопка подключения справа внизу */}
        {consultation.consultation_type === 'offline' && consultation.lifecycle_status === 'ready' ? (
          <div className="flex justify-end">
            <Button variant="dark" onClick={handleOpenTranscription} size="md" className="w-[270px]">
              Открыть транскрипцию
            </Button>
          </div>
        ) : consultation.consultation_type !== 'offline' ? (
          <div className="flex justify-end">
            <Button
              variant="dark"
              onClick={() => consultation?.code && window.open(`/meet/${consultation.code}`, '_blank')}
              size="md"
              className="w-[270px]"
            >
              Подключиться к встрече
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
